<template>
    <VPanel @close="$emit('close', $event)">
        <VPanelRow center class="numeric-panel__row--secondary" v-if="!!numeric.archivedAt">
            <NoticeSvg class="numeric-panel__alert-icon"></NoticeSvg>

            <h4 class="numeric-panel__alert">
                {{ t('numeric-archived') }}
            </h4>
        </VPanelRow>
        <VPanelRow first>
            <VTextarea
                class="simple-textarea--h2"
                concurrency-mode
                debounce-mode
                :disabled="!canUpdatePrivateFields || disabled"
                :editable="editable"
                maxlength="512"
                :model-value="numeric.name"
                required
                @update:model-value="setNameAsync($event)"
            ></VTextarea>
        </VPanelRow>

        <VPanelRow padding v-if="task && taskLink">
            <VInlineLink class="prevent-close" :subject="t('attached-to-goal')" :to="taskLink" v-if="task">
                {{ task.title ? task.title : t('untitled') }}
            </VInlineLink>
        </VPanelRow>

        <VPanelRow padding separate>
            <VInfoList class="numeric-panel__info-list">
                <VInfoItem center :label="t('author')" required>
                    <VUserSelectorNew
                        class="v-select--primary v-select--invisible v-select--no-option-padding v-select--form-user-selector"
                        :model-value="[numeric.authorId]"
                        @selected="setAuthorAsync($event)"
                    ></VUserSelectorNew>
                </VInfoItem>

                <VInfoItem center :label="t('assignee')" required>
                    <VUserSelectorNew
                        class="v-select--primary v-select--invisible v-select--no-option-padding v-select--form-user-selector"
                        :model-value="numeric.assigneeId ? [numeric.assigneeId] : []"
                        @selected="setAssignedAsync($event)"
                    ></VUserSelectorNew>
                </VInfoItem>

                <VInfoItem center :label="t('goal', 1)">
                    <VSelect
                        class="v-select--primary"
                        :disabled="!canUpdatePrivateFields || disabled"
                        :filterable="false"
                        :label="taskSelector.label"
                        :model-value="task"
                        :options="taskSelector.options.value"
                        :placeholder="t('default-placeholder', 10)"
                        @open="taskSelector.prefetch"
                        @option:selected="setTaskAsync(taskSelector.reduce($event))"
                        @search="taskSelector.searchAsync"
                    >
                        <template #selected-option>
                            <!-- Don't display the selected option until the backend data is updated and pulled after refresh -->
                            <VInlineLink
                                class="inline-link--user-selectedor-option"
                                :to="taskLink"
                                @mousedown.stop
                                v-if="task && taskLink"
                            >
                                {{ task.title ? task.title : t('untitled') }}
                            </VInlineLink>
                        </template>
                    </VSelect>
                </VInfoItem>

                <VInfoItem center :label="t('numeric.year')" required>
                    <VDatePicker
                        :config="{
                            yearPicker: true,
                            autoApply: true,
                            clearable: false,
                            startDate: new Date(),
                            minDate: new Date(),
                            maxDate: new Date(new Date().getFullYear() + 5, 1),
                            textInput: {
                                format: 'yyyy',
                            },
                            disabled: !canUpdatePrivateFields || disabled,
                        }"
                        :editable="editable && !completion.current.hasCommitedQuarters"
                        :model-value="numeric.year.toString()"
                        @edit-request="migrateNumericWithConfirm(numeric)"
                        @update:model-value="setYearAsync($event)"
                    ></VDatePicker>
                </VInfoItem>

                <VInfoItem center gap :label="t('numeric.dividend-name')" required>
                    <VSelect
                        class="v-select--primary"
                        :disabled="!canUpdatePrivateFields || disabled"
                        :filterable="false"
                        :label="counterSelector.label"
                        :model-value="numeric.counter"
                        :options="counterSelector.options.value"
                        :placeholder="t('default-placeholder', 1)"
                        taggable
                        @open="counterSelector.prefetch"
                        @option:selected="showCounterControl($event, counterControl, numeric.counterDivisor?.units)"
                        @search="counterSelector.searchAsync"
                    >
                    </VSelect>

                    <VButton
                        class="button--default button--secondary"
                        @click="
                            showCounterControl(
                                numeric.counter,
                                counterControl,
                                numeric.counter?.units,
                                numeric.counterTagName,
                                numeric.counterTagValue,
                            )
                        "
                        v-if="numeric.counter"
                    >
                        {{ t('control') }}
                    </VButton>
                </VInfoItem>

                <VInfoItem center gap :label="t('numeric.divisor-name')">
                    <VSelect
                        class="v-select--primary"
                        clearable
                        deselectFromDropdown
                        :disabled="!canUpdatePrivateFields || disabled"
                        :filterable="false"
                        :label="counterSelector.label"
                        :model-value="numeric.counterDivisor"
                        :options="counterSelector.options.value"
                        :placeholder="t('default-placeholder', 0)"
                        taggable
                        @open="counterSelector.prefetch"
                        @option:deselected="deselectCounterDivisor"
                        @option:selected="showCounterControl($event, counterDivisorControl, numeric.counter?.units)"
                        @search="counterSelector.searchAsync"
                    >
                    </VSelect>

                    <VButton
                        class="button--default button--secondary"
                        @click="
                            showCounterControl(
                                numeric.counterDivisor,
                                counterDivisorControl,
                                numeric.counter?.units,
                                numeric.counterDivisorTagName,
                                numeric.counterDivisorTagValue,
                            )
                        "
                        v-if="numeric.counterDivisor"
                    >
                        {{ t('control') }}
                    </VButton>
                </VInfoItem>

                <VInfoItem center :label="t('numeric.condition')" required>
                    <VSelect
                        class="v-select--primary"
                        :disabled="!canUpdatePrivateFields || disabled"
                        :filterable="false"
                        :label="conditionSelector.label"
                        :model-value="numeric.condition ? conditionSelector.map!(numeric.condition) : null"
                        :options="conditionSelector.options"
                        :placeholder="t('default-placeholder', 1)"
                        @option:selected="setConditionAsync(conditionSelector.reduce!($event))"
                    >
                    </VSelect>
                </VInfoItem>

                <VInfoItem center :label="t('numeric.view-mode')">
                    <VSelect
                        class="v-select--primary"
                        :disabled="!canUpdatePrivateFields || disabled"
                        :filterable="false"
                        :label="viewModeSelector.label"
                        :model-value="numeric.viewMode ? viewModeSelector.map!(numeric.viewMode) : null"
                        :options="viewModeSelector.options"
                        :placeholder="t('default-placeholder', 1)"
                        @option:selected="setViewModeAsync(viewModeSelector.reduce!($event))"
                    >
                    </VSelect>
                </VInfoItem>

                <small
                    class="text text--h7 text--negative"
                    v-if="
                        numeric.counter?.units &&
                        numeric.counterDivisor?.units &&
                        numeric.counter.units.toLowerCase() !== numeric.counterDivisor.units.toLowerCase()
                    "
                >
                    {{ t('numeric-counter.units-not-match-description') }}
                </small>
            </VInfoList>
        </VPanelRow>

        <VPanelRow padding separate separate-bottom v-if="isFullfiled">
            <VNumericQuarterTable :view-mode="numeric.viewMode ? numeric.viewMode : NumericViewMode.Proportion">
                <template #default="{ viewMode }">
                    <VNumericQuarterTableColumn
                        :actual-value="numeric.actualValueQ1"
                        :can-update-private-fields="canUpdatePrivateFields"
                        :can-update-public-fields="canUpdatePublicFields"
                        :committed="!!numeric.committedAtQ1"
                        :disabled="disabled"
                        :expected-value="numeric.expectedValueQ1"
                        :quarter="1"
                        :quarter-completion="completion.quarter1"
                        :view-mode="viewMode"
                        @add-value="
                            showNumericValueControl(
                                1,
                                completion.quarter1,
                                numeric.actualValueQ1,
                                numeric.expectedValueQ1,
                            )
                        "
                        @commit-value="commitAsync(1)"
                        @update:expected-value="setExpectedValueAsync({ expectedValueQ1: $event })"
                    >
                    </VNumericQuarterTableColumn>

                    <VNumericQuarterTableColumn
                        :actual-value="numeric.actualValueQ2"
                        :can-update-private-fields="canUpdatePrivateFields"
                        :can-update-public-fields="canUpdatePublicFields"
                        :committed="!!numeric.committedAtQ2"
                        :disabled="disabled"
                        :expected-value="numeric.expectedValueQ2"
                        :quarter="2"
                        :quarter-completion="completion.quarter2"
                        :view-mode="viewMode"
                        @add-value="
                            showNumericValueControl(
                                2,
                                completion.quarter2,
                                numeric.actualValueQ2,
                                numeric.expectedValueQ2,
                            )
                        "
                        @commit-value="commitAsync(2)"
                        @update:expected-value="setExpectedValueAsync({ expectedValueQ2: $event })"
                    >
                    </VNumericQuarterTableColumn>

                    <VNumericQuarterTableColumn
                        :actual-value="numeric.actualValueQ3"
                        :can-update-private-fields="canUpdatePrivateFields"
                        :can-update-public-fields="canUpdatePublicFields"
                        :committed="!!numeric.committedAtQ3"
                        :disabled="disabled"
                        :expected-value="numeric.expectedValueQ3"
                        :quarter="3"
                        :quarter-completion="completion.quarter3"
                        :view-mode="viewMode"
                        @add-value="
                            showNumericValueControl(
                                3,
                                completion.quarter3,
                                numeric.actualValueQ3,
                                numeric.expectedValueQ3,
                            )
                        "
                        @commit-value="commitAsync(3)"
                        @update:expected-value="setExpectedValueAsync({ expectedValueQ3: $event })"
                    >
                    </VNumericQuarterTableColumn>

                    <VNumericQuarterTableColumn
                        :actual-value="numeric.actualValueQ4"
                        :can-update-private-fields="canUpdatePrivateFields"
                        :can-update-public-fields="canUpdatePublicFields"
                        :committed="!!numeric.committedAtQ4"
                        :disabled="disabled"
                        :expected-value="numeric.expectedValueQ4"
                        :quarter="4"
                        :quarter-completion="completion.quarter4"
                        :view-mode="viewMode"
                        @add-value="
                            showNumericValueControl(
                                4,
                                completion.quarter4,
                                numeric.actualValueQ4,
                                numeric.expectedValueQ4,
                            )
                        "
                        @commit-value="commitAsync(4)"
                        @update:expected-value="setExpectedValueAsync({ expectedValueQ4: $event })"
                    >
                    </VNumericQuarterTableColumn>
                </template>
            </VNumericQuarterTable>
        </VPanelRow>

        <VPanelRow :label="t('remark')" label-separate padding separate>
            <VContentEditable
                class="simple-contenteditable--great simple-contenteditable--unlimited inline-100-x inline-fluid"
                concurrency-mode
                debounce-mode
                :disabled="!canUpdatePrivateFields || disabled"
                :editable="editable"
                maxlength="4096"
                :model-value="numeric.remark"
                @update:model-value="setRemarkAsync($event)"
            ></VContentEditable>
        </VPanelRow>

        <VPanelRow :label="t('comment')" label-separate padding separate>
            <VContentEditable
                class="simple-contenteditable--great simple-contenteditable--unlimited inline-100-x inline-fluid"
                concurrency-mode
                debounce-mode
                :disabled="!canUpdatePublicFields || disabled"
                :editable="editable"
                maxlength="4096"
                :model-value="numeric.comment"
                @update:model-value="setCommentAsync($event)"
            ></VContentEditable>
        </VPanelRow>

        <VPanelLayer v-model:visible="counterControl.visible" header @update:visible="counterControl.context = null">
            <template v-slot="{ close }">
                <VNumericCounterControl
                    :counter="counterControl.context.counter"
                    :tag-name="counterControl.context.tagName"
                    :tag-value="counterControl.context.tagValue"
                    :units="counterControl.context.units"
                    @close="close"
                    @submit="setCounterAsync"
                    v-if="counterControl.context"
                ></VNumericCounterControl>
            </template>
        </VPanelLayer>

        <VPanelLayer
            v-model:visible="counterDivisorControl.visible"
            header
            @update:visible="counterDivisorControl.context = null"
        >
            <template v-slot="{ close }">
                <VNumericCounterControl
                    :counter="counterDivisorControl.context.counter"
                    :tag-name="counterDivisorControl.context.tagName"
                    :tag-value="counterDivisorControl.context.tagValue"
                    :units="counterDivisorControl.context.units"
                    @close="close"
                    @submit="setCounterDivisorAsync"
                    v-if="counterDivisorControl.context"
                ></VNumericCounterControl>
            </template>
        </VPanelLayer>

        <VPanelLayer
            v-model:visible="numericValueControl.visible"
            header
            @update:visible="numericValueControl.context = null"
            v-if="numericValueControl.context"
        >
            <template v-slot="{ close }">
                <VNumericCounterValueControl
                    :actual-value="numericValueControl.context.actualValue"
                    :counter="numeric.counter"
                    :counter-divisor="numeric.counterDivisor"
                    :expected-value="numericValueControl.context.expectedValue"
                    :numeric="numeric"
                    :quarter="numericValueControl.context.quarter"
                    :quarter-completion="numericValueControl.context.quarterCompletion"
                    @close="close"
                    @submit="bulkInsertAsync"
                    v-if="numericValueControl.context && numeric.counter"
                ></VNumericCounterValueControl>
            </template>
        </VPanelLayer>
    </VPanel>
</template>
<script setup lang="ts">
// Components
import VPanel from './VPanel.vue';
import VPanelRow from './VPanelRow.vue';
import VPanelLayer from './VPanelLayer.vue';
import VInfoList from './VInfoList.vue';
import VInfoItem from './VInfoItem.vue';
import VSelect from './VSelect.vue';
import VTextarea from './VTextarea.vue';
import VDatePicker from './VDatePicker.vue';
import VInlineLink from './VInlineLink.vue';
import VContentEditable from './VContentEditable.vue';
import VNumericCounterValueControl from './VNumericCounterValueControl.vue';
import VNumericCounterControl from './VNumericCounterControl.vue';
import VNumericQuarterTable from './VNumericQuarterTable.vue';
import VNumericQuarterTableColumn from './VNumericQuarterTableColumn.vue';

// SVG
import NoticeSvg from '@/assets/notice.svg';

// Other
import INumeric from '@/core/Models/INumeric';
import { defineProps, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useNumericCompletion,
    useNumericContextMenu,
    useNumericForm,
    useNumericPermissions,
} from '@/mixins/NumericApi';
import ITask from '@/core/Models/ITask';
import { useTaskLink } from '@/mixins/TaskApi';
import VUserSelectorNew from './VUserSelectorNew.vue';
import {
    useNumericConditionSelector,
    useNumericCounterSelector,
    useNumericViewModeSelector,
    useTaskSelector,
} from '@/mixins/SelectorsApi';
import TaskType from '@/core/Values/TaskType';
import INumericCounter from '@/core/Models/INumericCounter';
import VButton from './VButton.vue';
import { INumericQuarterCompletion } from '@/mixins/NumericApi';
import { useNumericCounterValueApi } from '@/mixins/NumericCounterValueApi';
import QuarterNumber from '@/core/Values/QuarterNumber';
import NumericViewMode from '@/core/Values/NumericViewMode';

interface CommitControlContext {
    quarter: QuarterNumber;
    actualValue: number;
    expectedValue: number;
    quarterCompletion: INumericQuarterCompletion;
}

interface CounterControlContext {
    counter: INumericCounter;
    units?: string;
    tagName?: string | null;
    tagValue?: string | null;
}

const { t } = useI18n();
const props = defineProps({
    task: { type: Object as PropType<ITask | null>, default: null },
    numeric: { type: Object as PropType<INumeric>, required: true },
});
const { numeric, task } = toRefs(props);

const {
    editable,
    disabled,
    isFullfiled,
    commitAsync,
    setTaskAsync,
    setAuthorAsync,
    setAssignedAsync,
    setCounterAsync,
    setCounterDivisorAsync,
    setYearAsync,
    setNameAsync,
    setRemarkAsync,
    setCommentAsync,
    setExpectedValueAsync,
    setConditionAsync,
    setViewModeAsync,
} = useNumericForm(numeric);
const { completion } = useNumericCompletion(numeric);
const { bulkInsertAsync } = useNumericCounterValueApi();
const { canUpdatePublicFields, canUpdatePrivateFields } = useNumericPermissions(numeric);
const taskLink = useTaskLink(task);
const taskSelector = useTaskSelector(
    ref({
        whereType: [TaskType.Goal],
    }),
);
const counterSelector = useNumericCounterSelector();
const conditionSelector = useNumericConditionSelector();
const viewModeSelector = useNumericViewModeSelector();

const { migrateNumericWithConfirm } = useNumericContextMenu();

let counterControl = ref({
    visible: false,
    context: null as CounterControlContext | null,
});

let counterDivisorControl = ref({
    visible: false,
    context: null as CounterControlContext | null,
});

let numericValueControl = ref({
    visible: false,
    context: null as CommitControlContext | null,
});

watch(
    () => numeric.value,
    () => {
        counterControl.value = { visible: false, context: null };
        counterDivisorControl.value = { visible: false, context: null };
        numericValueControl.value = { visible: false, context: null };
    },
);

function showCounterControl(
    event: INumericCounter | string,
    control: typeof counterDivisorControl.value,
    units?: string,
    tagName?: string | null,
    tagValue?: string | null,
) {
    if (typeof event === 'string') {
        event = { name: event } as INumericCounter;
    }

    event = event.id
        ? event
        : {
              id: -1,
              key: '',
              name: event.name,
              units: units ?? '',
          };

    let counter = event;

    if (!tagName && !tagValue && numeric.value.assigneeId) {
        tagName = 'kpi-numerics.system.user';
        tagValue = numeric.value.assigneeId;
    }

    control.visible = true;
    control.context = {
        counter,
        units,
        tagName,
        tagValue,
    };
}

function showNumericValueControl(
    quarter: QuarterNumber,
    quarterCompletion: INumericQuarterCompletion,
    actualValue: number,
    expectedValue: number,
) {
    numericValueControl.value.visible = true;
    numericValueControl.value.context = {
        quarter,
        actualValue,
        expectedValue,
        quarterCompletion,
    };
}

function deselectCounterDivisor() {
    setCounterDivisorAsync({
        counterId: 0,
        numericTagName: '',
        numericTagValue: '',
    });
}
</script>

<style lang="scss">
.numeric-panel {
    &__row {
        width: 100%;
        padding: 0 0.5rem;

        @media (min-width: #{$breakpoint-tablet + 1}) {
            padding: 0 1rem;
        }

        &--title {
            padding-top: 1rem;
        }

        &--close-button {
            position: relative;
            padding-right: 3.5rem;

            color: var(--text-black-primary);

            @media (min-width: #{$breakpoint-tablet + 1}) {
                padding-right: 4rem;
            }
        }

        &--padding {
            padding: 0 1rem;

            @media (min-width: #{$breakpoint-tablet + 1}) {
                padding: 0 2rem;
            }
        }

        &--separate {
            margin-top: 1.25rem;
        }

        &--secondary {
            background-color: var(--background-color);
            --background-color: var(--background-secondary);
        }
    }

    &__alert-icon {
        width: 1.75rem;
        height: 1.75rem;
        margin-right: 0.25rem;

        color: var(--color-negative);
    }

    &__alert {
        margin: 0;
        padding: 1rem 0;

        @include h4();
        color: var(--color-negative);
    }

    &__button {
        &--close {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
            padding: 0.5rem;

            color: var(--text-black-primary);

            @media (min-width: #{$breakpoint-tablet + 1}) {
                right: 1rem;
            }
        }
    }

    &__info-list {
        @include h7(false);

        color: var(--text-black-secondary);
    }
}
</style>
